import { useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import {
  DataTable,
  DataTableColumnSettings,
  DataTableProvider,
  type TableColumnDef,
} from "../data-table";
import {
  MessageSquare,
  Phone,
  KeyRound,
  ShieldCheck,
  User,
  FileText,
  Hash,
  Link2,
  Loader2,
  Clock,
  Fingerprint,
  Globe,
  Monitor,
  GitCompare,
} from "lucide-react";
import type { SmsLog, SmsLogChange } from "../../services/api";
import {
  formatDisplayValue,
  formatLogTimestamp,
  formatTypeLabel,
  getSmsAction,
  getSmsChanges,
  getSmsConfig,
  getSmsCorrelationId,
  getSmsIpAddress,
  getSmsIsSignup,
  getSmsObjectPk,
  getSmsOtp,
  getSmsRecipient,
  getSmsResource,
  getSmsStatus,
  getSmsTenantName,
  getSmsTimestamp,
  getSmsTitle,
  getSmsType,
  getSmsUser,
  getSmsUserAgent,
  getSmsVerifiedAt,
} from "../../lib/communicationLogUtils";

interface SmsLogDetailsModalProps {
  open: boolean;
  log: SmsLog | null;
  loading: boolean;
  onOpenChange: (open: boolean) => void;
}

const changeTableHeadClass =
  "h-8 text-[11px] font-bold uppercase tracking-wide text-zinc-500";

export function SmsLogDetailsModal({
  open,
  log,
  loading,
  onOpenChange,
}: SmsLogDetailsModalProps) {
  const timestamp = log ? formatLogTimestamp(getSmsTimestamp(log)) : "—";
  const verifiedAt = log?.verified_at
    ? formatLogTimestamp(getSmsVerifiedAt(log))
    : "—";
  const statusLabel = log ? getSmsStatus(log) : "—";
  const actionLabel = log ? getSmsAction(log) : "—";
  const changes = log ? getSmsChanges(log) : [];

  const changeColumns = useMemo<TableColumnDef<SmsLogChange>[]>(
    () => [
      {
        id: "field",
        label: "Field",
        width: 160,
        headerClassName: changeTableHeadClass,
        renderCell: row => (
          <span className="font-medium capitalize text-zinc-800">
            {row.field}
          </span>
        ),
      },
      {
        id: "from",
        label: "From",
        width: 200,
        headerClassName: changeTableHeadClass,
        cellClassName: "max-w-[220px]",
        renderCell: row => (
          <span
            className="block truncate font-mono text-xs text-zinc-500"
            title={formatDisplayValue(row.from)}
          >
            {formatDisplayValue(row.from)}
          </span>
        ),
      },
      {
        id: "to",
        label: "To",
        width: 280,
        headerClassName: changeTableHeadClass,
        cellClassName: "max-w-[320px]",
        renderCell: row => (
          <span
            className="block truncate text-sm text-zinc-800"
            title={formatDisplayValue(row.to)}
          >
            {formatDisplayValue(row.to)}
          </span>
        ),
      },
    ],
    []
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl gap-0 overflow-hidden p-0 sm:rounded-xl [&>button]:right-5 [&>button]:top-5 [&>button]:z-10 [&>button]:text-zinc-500 [&>button]:opacity-100 [&>button]:hover:text-zinc-900">
        <DialogHeader className="border-b border-zinc-200 bg-white px-6 pb-4 pr-14 pt-5 text-left">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <DialogTitle className="flex items-center gap-3 text-lg font-semibold tracking-tight text-zinc-900">
              <span
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-[#FCECEC]"
                aria-hidden
              >
                <MessageSquare
                  className="h-5 w-5 text-[#8B3232]"
                  strokeWidth={2.25}
                />
              </span>
              SMS / OTP Log Details
            </DialogTitle>
            {log && !loading && actionLabel !== "—" && (
              <span className="inline-flex items-center gap-2 rounded-full border border-[#f0dede] bg-[#FDF7F7] px-3.5 py-1 text-sm font-semibold capitalize text-[#8B3232]">
                <span
                  className="h-2 w-2 shrink-0 rounded-full bg-[#c53030]"
                  aria-hidden
                />
                {actionLabel}
              </span>
            )}
          </div>
        </DialogHeader>

        {loading ? (
          <div className="flex h-[400px] flex-col items-center justify-center gap-4 bg-white">
            <Loader2 className="h-12 w-12 animate-spin text-[#8B3232]" />
            <p className="animate-pulse font-medium text-muted-foreground">
              Fetching SMS log details...
            </p>
          </div>
        ) : (
          log && (
            <>
              <div className="max-h-[min(80vh,730px)] overflow-y-auto bg-white">
                {/* Summary */}
                <div className="border-b border-zinc-200/80 bg-[#FDF7F7] px-6 py-5">
                  <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between sm:gap-8">
                    <div className="min-w-0 space-y-1 sm:flex-1">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#8B3232]">
                        Phone number
                      </p>
                      <p className="text-xl font-semibold tracking-tight text-zinc-900 sm:text-2xl">
                        {getSmsRecipient(log)}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-end gap-x-10 gap-y-4 sm:shrink-0 sm:justify-end sm:gap-x-12 md:gap-x-16">
                      <div className="min-w-0">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                          Created
                        </p>
                        <p className="text-sm font-semibold text-zinc-900">
                          {timestamp}
                        </p>
                      </div>
                      <div className="min-w-0">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                          OTP type
                        </p>
                        <p className="text-sm font-semibold capitalize text-zinc-900">
                          {formatTypeLabel(getSmsType(log))}
                        </p>
                      </div>
                      <div className="min-w-0">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                          Status
                        </p>
                        <p className="text-sm font-semibold capitalize text-zinc-900">
                          {statusLabel}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-6 px-6 py-5 md:grid-cols-2">
                  {/* OTP details */}
                  <div className="rounded-xl border border-zinc-200 bg-white p-5 shadow-sm">
                    <h3 className="mb-3 flex items-center gap-2.5 text-base font-bold text-zinc-900">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[#FCECEC]">
                        <KeyRound className="h-4 w-4 text-[#8B3232]" />
                      </span>
                      OTP details
                    </h3>
                    <hr className="mb-4 border-zinc-200" />
                    <div className="grid gap-5">
                      <DetailItem
                        icon={<Phone className="h-3.5 w-3.5" />}
                        label="Phone number"
                        value={getSmsRecipient(log)}
                      />
                      <DetailItem
                        icon={<Fingerprint className="h-3.5 w-3.5" />}
                        label="OTP code"
                        value={getSmsOtp(log)}
                      />
                      <DetailItem
                        icon={<ShieldCheck className="h-3.5 w-3.5" />}
                        label="OTP type"
                        value={formatTypeLabel(getSmsType(log))}
                      />
                      <DetailItem
                        icon={<User className="h-3.5 w-3.5" />}
                        label="Signup flow"
                        value={getSmsIsSignup(log)}
                      />
                      <DetailItem
                        icon={<Clock className="h-3.5 w-3.5" />}
                        label="Verified at"
                        value={verifiedAt}
                      />
                    </div>
                  </div>

                  {/* Audit */}
                  <div className="rounded-xl border border-zinc-200 bg-white p-5 shadow-sm">
                    <h3 className="mb-3 flex items-center gap-2.5 text-base font-bold text-zinc-900">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[#FCECEC]">
                        <FileText className="h-4 w-4 text-[#8B3232]" />
                      </span>
                      Audit information
                    </h3>
                    <hr className="mb-4 border-zinc-200" />
                    <div className="grid gap-5">
                      <DetailItem
                        icon={<FileText className="h-3.5 w-3.5" />}
                        label="Action"
                        value={getSmsAction(log)}
                      />
                      <DetailItem
                        icon={<User className="h-3.5 w-3.5" />}
                        label="User"
                        value={getSmsUser(log)}
                      />
                      <DetailItem
                        icon={<Link2 className="h-3.5 w-3.5" />}
                        label="Resource"
                        value={getSmsResource(log)}
                      />
                      <DetailItem
                        icon={<Hash className="h-3.5 w-3.5" />}
                        label="Object PK"
                        value={getSmsObjectPk(log)}
                      />
                      <DetailItem
                        icon={<Hash className="h-3.5 w-3.5" />}
                        label="Log ID"
                        value={String(log.id)}
                      />
                      <DetailItem
                        icon={<Link2 className="h-3.5 w-3.5" />}
                        label="Correlation ID"
                        value={getSmsCorrelationId(log)}
                      />
                    </div>
                  </div>

                  {/* Request context */}
                  <div className="rounded-xl border border-zinc-200 bg-white p-5 shadow-sm md:col-span-2">
                    <h3 className="mb-3 flex items-center gap-2.5 text-base font-bold text-zinc-900">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[#FCECEC]">
                        <Globe className="h-4 w-4 text-[#8B3232]" />
                      </span>
                      Request context
                    </h3>
                    <hr className="mb-4 border-zinc-200" />
                    <div className="grid gap-5 sm:grid-cols-2">
                      <DetailItem
                        icon={<Globe className="h-3.5 w-3.5" />}
                        label="IP address"
                        value={getSmsIpAddress(log)}
                      />
                      <DetailItem
                        icon={<User className="h-3.5 w-3.5" />}
                        label="Performed by"
                        value={getSmsTenantName(log)}
                      />
                      <div className="sm:col-span-2">
                        <DetailItem
                          icon={<Monitor className="h-3.5 w-3.5" />}
                          label="User agent"
                          value={getSmsUserAgent(log)}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Event title */}
                  <div className="md:col-span-2">
                    <h3 className="mb-3 flex items-center gap-2.5 text-base font-bold text-zinc-900">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[#FCECEC]">
                        <MessageSquare className="h-4 w-4 text-[#8B3232]" />
                      </span>
                      Event description
                    </h3>
                    <div className="border-y border-zinc-200 py-4 pl-4 pr-4">
                      <p className="border-l-[3px] border-[#8B3232] pl-4 text-sm font-medium leading-relaxed text-zinc-500">
                        {getSmsTitle(log) !== "—"
                          ? getSmsTitle(log)
                          : getSmsConfig(log)}
                      </p>
                    </div>
                  </div>

                  {/* Field changes */}
                  <DataTableProvider
                    tableId="super-admin-sms-log-changes"
                    columns={changeColumns}
                  >
                    <div className="space-y-3 md:col-span-2">
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="flex items-center gap-2.5 text-base font-bold text-zinc-900">
                          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[#FCECEC]">
                            <GitCompare className="h-4 w-4 text-[#8B3232]" />
                          </span>
                          Field changes
                          {changes.length > 0 && (
                            <span className="ml-1 text-sm font-normal text-muted-foreground">
                              ({changes.length})
                            </span>
                          )}
                        </h3>
                        <DataTableColumnSettings columns={changeColumns} />
                      </div>
                      <div className="overflow-x-auto rounded-lg border border-zinc-200">
                        <DataTable
                          embedded
                          hideToolbar
                          tableId="super-admin-sms-log-changes"
                          columns={changeColumns}
                          data={changes}
                          getRowKey={row =>
                            `${row.field}-${row.from ?? ""}-${row.to ?? ""}`
                          }
                          emptyMessage="No field changes recorded for this event."
                          headerRowClassName="border-b border-zinc-200 bg-zinc-50/90 hover:bg-zinc-50/90"
                          rowClassName={() => "border-zinc-100"}
                        />
                      </div>
                    </div>
                  </DataTableProvider>
                </div>
              </div>

              <DialogFooter className="gap-3 border-t border-zinc-200 bg-white px-6 py-4 sm:justify-end">
                <Button
                  type="button"
                  variant="ghost"
                  className="text-zinc-700 hover:bg-zinc-100 hover:text-zinc-900"
                  onClick={() => onOpenChange(false)}
                >
                  Close
                </Button>
              </DialogFooter>
            </>
          )
        )}
      </DialogContent>
    </Dialog>
  );
}

function DetailItem({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-zinc-100 text-zinc-400 [&_svg]:text-zinc-400">
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
          {label}
        </p>
        <p className="break-all text-sm font-semibold text-zinc-900">{value}</p>
      </div>
    </div>
  );
}
